import type {
  SearchMeasureFilter,
  SearchPlacementFilter,
  SearchTypeFilterOption,
} from "../../../stores/store";
import { Measures } from "../../../types/entities";

export const SEARCH_PRESETS_STORAGE_KEY = "search.filterPresets.v1";
export const MAX_SEARCH_PRESETS = 6;

export interface SearchFilterPreset {
  id: string;
  label: string;
  measureFilter: SearchMeasureFilter;
  placementFilter: SearchPlacementFilter;
  typeFilter: string;
}

export interface SearchMeasureOption {
  value: SearchMeasureFilter;
  label: string;
}

export interface SearchPlacementOption {
  value: SearchPlacementFilter;
  label: string;
}

export interface SearchFiltersSnapshot {
  measureFilter: SearchMeasureFilter;
  placementFilter: SearchPlacementFilter;
  typeFilter: string;
}

export const QUICK_SEARCH_PRESETS: SearchFilterPreset[] = [
  {
    id: "quick_in_box",
    label: "В коробках",
    measureFilter: "all",
    placementFilter: "inBox",
    typeFilter: "all",
  },
  {
    id: "quick_without_box",
    label: "Без коробки",
    measureFilter: "all",
    placementFilter: "withoutBox",
    typeFilter: "all",
  },
  {
    id: "quick_all",
    label: "Все",
    measureFilter: "all",
    placementFilter: "all",
    typeFilter: "all",
  },
];

export const SEARCH_MEASURE_OPTIONS: SearchMeasureOption[] = [
  { value: "all", label: "Все меры" },
  { value: Measures.quantity, label: "Количество" },
  { value: Measures.weight, label: "Вес" },
  { value: Measures.liters, label: "Литры" },
];

export const SEARCH_PLACEMENT_OPTIONS: SearchPlacementOption[] = [
  { value: "all", label: "Любое место" },
  { value: "inBox", label: "В коробке" },
  { value: "withoutBox", label: "Без коробки" },
];

export function isPresetApplied(
  preset: SearchFilterPreset,
  filters: SearchFiltersSnapshot,
): boolean {
  return (
    filters.measureFilter === preset.measureFilter &&
    filters.placementFilter === preset.placementFilter &&
    filters.typeFilter === preset.typeFilter
  );
}

export function createPresetId(): string {
  if (
    typeof globalThis.crypto !== "undefined" &&
    typeof globalThis.crypto.randomUUID === "function"
  ) {
    return `sp_${globalThis.crypto.randomUUID()}`;
  }

  return `sp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function getPresetSignature(preset: SearchFilterPreset): string {
  return [preset.measureFilter, preset.placementFilter, preset.typeFilter].join(
    "|",
  );
}

function createCurrentPreset(
  filters: SearchFiltersSnapshot,
): SearchFilterPreset {
  return {
    id: "",
    label: "",
    measureFilter: filters.measureFilter,
    placementFilter: filters.placementFilter,
    typeFilter: filters.typeFilter,
  };
}

export function buildPresetDefaultLabel(input: {
  preset: SearchFilterPreset;
  measureOptions: SearchMeasureOption[];
  placementOptions: SearchPlacementOption[];
  typeOptions: SearchTypeFilterOption[];
}): string {
  const parts: string[] = [];
  const measure = input.measureOptions.find(
    (option) => option.value === input.preset.measureFilter,
  );
  const placement = input.placementOptions.find(
    (option) => option.value === input.preset.placementFilter,
  );
  const type = input.typeOptions.find(
    (option) => option.id === input.preset.typeFilter,
  );

  if (measure && measure.value !== "all") {
    parts.push(measure.label);
  }
  if (placement && placement.value !== "all") {
    parts.push(placement.label);
  }
  if (input.preset.typeFilter !== "all") {
    parts.push(type?.name || `#${input.preset.typeFilter}`);
  }

  return parts.length > 0 ? parts.join(" · ") : "Мой пресет";
}

export function buildNextSavedPresets(input: {
  existingPresets: SearchFilterPreset[];
  filters: SearchFiltersSnapshot;
  measureOptions: SearchMeasureOption[];
  placementOptions: SearchPlacementOption[];
  typeOptions: SearchTypeFilterOption[];
  promptLabel: (defaultLabel: string) => string | null;
  maxPresets?: number;
  createId?: () => string;
}): SearchFilterPreset[] | null {
  const currentPreset = createCurrentPreset(input.filters);
  const defaultLabel = buildPresetDefaultLabel({
    preset: currentPreset,
    measureOptions: input.measureOptions,
    placementOptions: input.placementOptions,
    typeOptions: input.typeOptions,
  });
  const rawLabel = input.promptLabel(defaultLabel);
  if (rawLabel === null) {
    return null;
  }

  const normalizedPreset: SearchFilterPreset = {
    ...currentPreset,
    label: rawLabel.trim() || defaultLabel,
  };
  const currentSignature = getPresetSignature(normalizedPreset);
  const existingIndex = input.existingPresets.findIndex(
    (item) => getPresetSignature(item) === currentSignature,
  );
  const nextPresets = [...input.existingPresets];
  if (existingIndex >= 0) {
    const existing = nextPresets.splice(existingIndex, 1)[0];
    nextPresets.unshift({
      ...normalizedPreset,
      id: existing.id,
    });
  } else {
    nextPresets.unshift({
      ...normalizedPreset,
      id: (input.createId || createPresetId)(),
    });
  }

  return nextPresets.slice(0, input.maxPresets || MAX_SEARCH_PRESETS);
}

function isSearchMeasureFilter(value: unknown): value is SearchMeasureFilter {
  return (
    value === "all" ||
    value === Measures.liters ||
    value === Measures.weight ||
    value === Measures.quantity
  );
}

function isSearchPlacementFilter(
  value: unknown,
): value is SearchPlacementFilter {
  return value === "all" || value === "inBox" || value === "withoutBox";
}

function normalizePreset(
  item: unknown,
  createIdFn: () => string,
): SearchFilterPreset | null {
  if (!item || typeof item !== "object") {
    return null;
  }

  const data = item as Record<string, unknown>;
  const id =
    typeof data.id === "string" && data.id.trim()
      ? data.id.trim()
      : createIdFn();
  const label =
    typeof data.label === "string" && data.label.trim()
      ? data.label.trim()
      : "Мой пресет";
  const measureFilter = isSearchMeasureFilter(data.measureFilter)
    ? data.measureFilter
    : "all";
  const placementFilter = isSearchPlacementFilter(data.placementFilter)
    ? data.placementFilter
    : "all";
  const typeFilter =
    typeof data.typeFilter === "string" && data.typeFilter.trim()
      ? data.typeFilter.trim()
      : "all";

  return {
    id,
    label,
    measureFilter,
    placementFilter,
    typeFilter,
  };
}

export function loadSavedFilterPresets(input: {
  storage: Storage | undefined;
  storageKey?: string;
  maxPresets?: number;
  createId?: () => string;
}): SearchFilterPreset[] {
  if (!input.storage) {
    return [];
  }

  try {
    const raw = input.storage.getItem(
      input.storageKey || SEARCH_PRESETS_STORAGE_KEY,
    );
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .map((item) => normalizePreset(item, input.createId || createPresetId))
      .filter((item): item is SearchFilterPreset => item !== null)
      .slice(0, input.maxPresets || MAX_SEARCH_PRESETS);
  } catch {
    return [];
  }
}

export function persistSavedFilterPresets(input: {
  storage: Storage | undefined;
  presets: SearchFilterPreset[];
  storageKey?: string;
}): void {
  if (!input.storage) {
    return;
  }

  try {
    input.storage.setItem(
      input.storageKey || SEARCH_PRESETS_STORAGE_KEY,
      JSON.stringify(input.presets),
    );
  } catch {
    // Игнорируем ошибки квоты/доступа localStorage.
  }
}
